import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { DemoPlayingCard, Card } from './DemoPlayingCard';

// ============ Types ============
type Suit = '♠' | '♥' | '♦' | '♣';
type Rank = 'A' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | 'J' | 'Q' | 'K';

interface Hand {
  cards: Card[];
  bet: number;
  result?: 'win' | 'loss' | 'push' | 'blackjack';
}

type GamePhase = 'betting' | 'playing' | 'dealer-turn' | 'complete' | 'limit-reached';

interface DecisionFeedback {
  isCorrect: boolean;
  message: string;
  correctAction: string;
}

// ============ Constants ============
const SUITS: Suit[] = ['♠', '♥', '♦', '♣'];
const RANKS: Rank[] = ['A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K'];
const DEMO_HAND_LIMIT = 15;
const STARTING_BANKROLL = 1000;
const MIN_BET = 25;
const MAX_BET = 500;
const STORAGE_KEY = 'jk-demo-hands-played';

// ============ LocalStorage Helpers ============
interface DemoState {
  handsPlayed: number;
  bankroll: number;
  timestamp: number;
}

function loadDemoState(): DemoState {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const data = JSON.parse(stored);
      // Check if stored within last 24 hours
      if (data.timestamp && Date.now() - data.timestamp < 24 * 60 * 60 * 1000) {
        return {
          handsPlayed: data.handsPlayed || 0,
          bankroll: data.bankroll ?? STARTING_BANKROLL,
          timestamp: data.timestamp
        };
      }
    }
  } catch {
    // Ignore errors
  }
  return { handsPlayed: 0, bankroll: STARTING_BANKROLL, timestamp: Date.now() };
}

function saveDemoState(handsPlayed: number, bankroll: number) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      handsPlayed,
      bankroll,
      timestamp: Date.now()
    }));
  } catch {
    // Ignore errors
  }
}

// ============ Deck Utilities ============
function createDeck(): Card[] {
  const deck: Card[] = [];
  let id = 0;
  for (const suit of SUITS) {
    for (const rank of RANKS) {
      deck.push({ suit, rank, id: `${suit}-${rank}-${id++}`, faceUp: false });
    }
  }
  return deck;
}

function shuffleDeck(deck: Card[]): Card[] {
  const shuffled = [...deck];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

function createShoe(numDecks: number): Card[] {
  const shoe: Card[] = [];
  for (let i = 0; i < numDecks; i++) {
    shoe.push(...createDeck().map(card => ({ ...card, id: `${card.id}-d${i}` })));
  }
  return shuffleDeck(shoe);
}

function getCardValue(rank: Rank): number {
  if (rank === 'A') return 11;
  if (['J', 'Q', 'K'].includes(rank)) return 10;
  return parseInt(rank);
}

function getHandValue(cards: Card[]): { value: number; soft: boolean } {
  let value = 0;
  let aces = 0;
  for (const card of cards) {
    if (card.rank === 'A') {
      aces++;
      value += 11;
    } else {
      value += getCardValue(card.rank);
    }
  }
  while (value > 21 && aces > 0) {
    value -= 10;
    aces--;
  }
  return { value, soft: aces > 0 && value <= 21 };
}

function isBlackjack(cards: Card[]): boolean {
  return cards.length === 2 && getHandValue(cards).value === 21;
}

// ============ Simplified Basic Strategy ============
function getBasicStrategyAction(playerCards: Card[], dealerUpCard: Card): 'hit' | 'stand' | 'double' {
  const { value, soft } = getHandValue(playerCards);
  const dealerValue = getCardValue(dealerUpCard.rank);
  const canDouble = playerCards.length === 2;

  // Soft hands
  if (soft) {
    if (value >= 19) return 'stand';
    if (value === 18) {
      if (dealerValue >= 9) return 'hit';
      if (canDouble && dealerValue >= 3 && dealerValue <= 6) return 'double';
      return 'stand';
    }
    if (value === 17) {
      if (canDouble && dealerValue >= 3 && dealerValue <= 6) return 'double';
      return 'hit';
    }
    // Soft 13-16
    if (canDouble && dealerValue >= 5 && dealerValue <= 6) return 'double';
    return 'hit';
  }

  // Hard hands
  if (value >= 17) return 'stand';
  
  if (value >= 13 && value <= 16) {
    return dealerValue >= 2 && dealerValue <= 6 ? 'stand' : 'hit';
  }
  
  if (value === 12) {
    return dealerValue >= 4 && dealerValue <= 6 ? 'stand' : 'hit';
  }
  
  if (value === 11) {
    return canDouble ? 'double' : 'hit';
  }
  
  if (value === 10) {
    if (canDouble && dealerValue >= 2 && dealerValue <= 9) return 'double';
    return 'hit';
  }
  
  if (value === 9) {
    if (canDouble && dealerValue >= 3 && dealerValue <= 6) return 'double';
    return 'hit';
  }

  return 'hit';
}

function evaluateDecision(
  playerAction: 'hit' | 'stand' | 'double',
  playerCards: Card[],
  dealerUpCard: Card
): DecisionFeedback {
  const correctAction = getBasicStrategyAction(playerCards, dealerUpCard);
  const isCorrect = playerAction === correctAction || 
    // Allow hit when double is correct but they hit (acceptable)
    (correctAction === 'double' && playerAction === 'hit');

  if (isCorrect) {
    return {
      isCorrect: true,
      message: '✓ Good play!',
      correctAction
    };
  }

  const actionNames: Record<string, string> = {
    hit: 'Hit',
    stand: 'Stand', 
    double: 'Double'
  };

  return {
    isCorrect: false,
    message: `✗ Basic strategy says ${actionNames[correctAction]}`,
    correctAction
  };
}

// ============ Chip Config ============
interface ChipValue {
  value: number;
  color: string;
  textColor: string;
  pattern: string;
}

function getChips(): ChipValue[] {
  const stripePattern = (stripe: string, fill: string) =>
    [
      `radial-gradient(circle at center, ${fill} 0%, ${fill} 55%, transparent 55.5%)`,
      `repeating-conic-gradient(${stripe} 0deg 20deg, transparent 20deg 40deg)`,
      `radial-gradient(circle at center, ${fill} 0%, ${fill} 100%)`,
      'radial-gradient(circle at 30% 30%, rgba(255,255,255,0.12), transparent 48%)'
    ].join(', ');

  return [
    { value: 5, color: 'bg-red-600 border-red-800', textColor: 'text-white', pattern: stripePattern('rgba(255,255,255,0.18)', '#dc2626') },
    { value: 25, color: 'bg-green-600 border-green-800', textColor: 'text-white', pattern: stripePattern('rgba(255,255,255,0.18)', '#16a34a') },
    { value: 100, color: 'bg-gray-800 border-gray-950', textColor: 'text-white', pattern: stripePattern('rgba(255,255,255,0.18)', '#111827') },
    { value: 500, color: 'bg-purple-600 border-purple-800', textColor: 'text-white', pattern: stripePattern('rgba(255,255,255,0.18)', '#7c3aed') }
  ];
}

// ============ Main Component ============
interface DemoHandTrainerProps {
  onSignUpClick: () => void;
}

export const DemoHandTrainer: React.FC<DemoHandTrainerProps> = ({ onSignUpClick }) => {
  // Load state from localStorage on init
  const [initialState] = useState(() => loadDemoState());
  const [handsPlayed, setHandsPlayed] = useState(initialState.handsPlayed);
  
  // Game state
  const [deck, setDeck] = useState<Card[]>(() => createShoe(6));
  const [playerHand, setPlayerHand] = useState<Hand>({ cards: [], bet: 0 });
  const [dealerHand, setDealerHand] = useState<Hand>({ cards: [], bet: 0 });
  const [bankroll, setBankroll] = useState(initialState.bankroll);
  const [currentBet, setCurrentBet] = useState(0);
  const [betHistory, setBetHistory] = useState<number[]>([]);
  // Base bet placed for the current hand, before any double-down. Used to restore
  // the betting circle to the original amount on the next hand so a winning double
  // doesn't silently carry the doubled bet forward.
  const [baseBet, setBaseBet] = useState(0);
  const [phase, setPhase] = useState<GamePhase>(() => 
    initialState.handsPlayed >= DEMO_HAND_LIMIT ? 'limit-reached' : 'betting'
  );
  const [sessionStats, setSessionStats] = useState({ wins: 0, losses: 0, pushes: 0, net: 0 });
  const [isDealing, setIsDealing] = useState(false);
  const [lastFeedback, setLastFeedback] = useState<DecisionFeedback | null>(null);

  const chips = useMemo(() => getChips(), []);

  // Save state to localStorage whenever hands or bankroll changes
  useEffect(() => {
    saveDemoState(handsPlayed, bankroll);
    if (handsPlayed >= DEMO_HAND_LIMIT) {
      setPhase('limit-reached');
    }
  }, [handsPlayed, bankroll]);

  // Get dealer's up card (first face-up card)
  const dealerUpCard = dealerHand.cards.find(c => c.faceUp) || dealerHand.cards[0];

  // Deal initial hands
  const dealHand = useCallback(() => {
    if (currentBet > bankroll) {
      setLastFeedback({ isCorrect: false, message: 'Not enough chips for that bet.', correctAction: '' });
      return;
    }
    if (currentBet < MIN_BET) {
      setLastFeedback({ isCorrect: false, message: `Minimum bet is $${MIN_BET}.`, correctAction: '' });
      return;
    }
    if (handsPlayed >= DEMO_HAND_LIMIT) {
      setPhase('limit-reached');
      return;
    }

    setLastFeedback(null);
    setIsDealing(true);
    // Reshuffle a full 6-deck shoe well before it runs out so a hand never deals
    // from a near-empty deck. The demo has no running count to preserve.
    const needsReshuffle = deck.length < 80;
    const newDeck = needsReshuffle ? createShoe(6) : [...deck];
    
    // Draw 4 cards
    const playerCard1 = { ...newDeck.pop()!, faceUp: true };
    const dealerCard1 = { ...newDeck.pop()!, faceUp: true };
    const playerCard2 = { ...newDeck.pop()!, faceUp: true };
    const dealerCard2 = { ...newDeck.pop()!, faceUp: false }; // Hole card face down

    setDeck(newDeck);
    setPlayerHand({ cards: [playerCard1, playerCard2], bet: currentBet });
    setDealerHand({ cards: [dealerCard1, dealerCard2], bet: 0 });
    setBankroll(prev => prev - currentBet);
    setBaseBet(currentBet);

    setTimeout(() => {
      setIsDealing(false);
      // Check for player blackjack
      if (isBlackjack([playerCard1, playerCard2])) {
        // Reveal dealer hole card and resolve
        const revealedDealer = { cards: [dealerCard1, { ...dealerCard2, faceUp: true }], bet: 0 };
        setDealerHand(revealedDealer);
        
        setTimeout(() => {
          const dealerBJ = isBlackjack(revealedDealer.cards);
          if (dealerBJ) {
            // Push - keep bet for next hand
            setBankroll(prev => prev + currentBet);
            setPlayerHand(prev => ({ ...prev, result: 'push' }));
            setSessionStats(prev => ({ ...prev, pushes: prev.pushes + 1 }));
          } else {
            // Player wins 3:2
            const winnings = currentBet + Math.floor(currentBet * 1.5);
            setBankroll(prev => prev + winnings);
            setPlayerHand(prev => ({ ...prev, result: 'blackjack' }));
            setSessionStats(prev => ({ ...prev, wins: prev.wins + 1, net: prev.net + Math.floor(currentBet * 1.5) }));
          }
          setHandsPlayed(prev => prev + 1);
          setPhase('complete');
        }, 500);
      } else {
        setPhase('playing');
      }
    }, 600);
  }, [currentBet, bankroll, deck, handsPlayed]);

  // Player actions with feedback
  const hit = useCallback(() => {
    if (phase !== 'playing' || isDealing || !dealerUpCard) return;
    
    // Evaluate decision
    const feedback = evaluateDecision('hit', playerHand.cards, dealerUpCard);
    setLastFeedback(feedback);
    
    const card = { ...deck[deck.length - 1], faceUp: true };
    setDeck(prev => prev.slice(0, -1));
    
    const newCards = [...playerHand.cards, card];
    setPlayerHand(prev => ({ ...prev, cards: newCards }));

    const { value } = getHandValue(newCards);
    if (value > 21) {
      // Bust - lose bet, must rebet
      setPlayerHand(prev => ({ ...prev, result: 'loss' }));
      setSessionStats(prev => ({ ...prev, losses: prev.losses + 1, net: prev.net - currentBet }));
      setHandsPlayed(prev => prev + 1);
      setDealerHand(prev => ({
        ...prev,
        cards: prev.cards.map(c => ({ ...c, faceUp: true }))
      }));
      // Clear bet on loss
      setCurrentBet(0);
      setBetHistory([]);
      setPhase('complete');
    }
  }, [phase, isDealing, deck, playerHand.cards, currentBet, dealerUpCard]);

  const stand = useCallback(() => {
    if (phase !== 'playing' || isDealing || !dealerUpCard) return;
    
    // Evaluate decision
    const feedback = evaluateDecision('stand', playerHand.cards, dealerUpCard);
    setLastFeedback(feedback);
    
    setPhase('dealer-turn');
    
    // Reveal hole card
    setDealerHand(prev => ({
      ...prev,
      cards: prev.cards.map(c => ({ ...c, faceUp: true }))
    }));
  }, [phase, isDealing, playerHand.cards, dealerUpCard]);

  const doubleDown = useCallback(() => {
    if (phase !== 'playing' || isDealing || playerHand.cards.length !== 2 || !dealerUpCard) return;
    if (currentBet > bankroll) return;

    // Evaluate decision
    const feedback = evaluateDecision('double', playerHand.cards, dealerUpCard);
    setLastFeedback(feedback);

    const newBet = currentBet * 2;
    setBankroll(prev => prev - currentBet);
    setPlayerHand(prev => ({ ...prev, bet: newBet }));

    // Draw one card
    const card = { ...deck[deck.length - 1], faceUp: true };
    setDeck(prev => prev.slice(0, -1));
    const newCards = [...playerHand.cards, card];
    setPlayerHand(prev => ({ ...prev, cards: newCards, bet: newBet }));

    const { value } = getHandValue(newCards);
    if (value > 21) {
      // Bust - lose doubled bet
      setPlayerHand(prev => ({ ...prev, result: 'loss' }));
      setSessionStats(prev => ({ ...prev, losses: prev.losses + 1, net: prev.net - newBet }));
      setHandsPlayed(prev => prev + 1);
      setDealerHand(prev => ({
        ...prev,
        cards: prev.cards.map(c => ({ ...c, faceUp: true }))
      }));
      // Clear bet on loss
      setCurrentBet(0);
      setBetHistory([]);
      setPhase('complete');
    } else {
      // Update current bet for tracking, then go to dealer
      setCurrentBet(newBet);
      setPhase('dealer-turn');
      setDealerHand(prev => ({
        ...prev,
        cards: prev.cards.map(c => ({ ...c, faceUp: true }))
      }));
    }
  }, [phase, isDealing, playerHand.cards, currentBet, bankroll, deck, dealerUpCard]);

  // Dealer plays out
  useEffect(() => {
    if (phase !== 'dealer-turn') return;

    const dealerPlay = async () => {
      let currentDealerCards = [...dealerHand.cards];
      let currentDeck = [...deck];
      
      const playDealer = () => {
        const { value, soft } = getHandValue(currentDealerCards);
        // Dealer hits on soft 17
        const soft17 = value === 17 && soft;
        
        if (value < 17 || soft17) {
          const card = { ...currentDeck.pop()!, faceUp: true };
          currentDealerCards = [...currentDealerCards, card];
          setDealerHand(prev => ({ ...prev, cards: currentDealerCards }));
          setDeck(currentDeck);
          setTimeout(playDealer, 600);
        } else {
          // Dealer done - resolve
          resolveHand(currentDealerCards);
        }
      };

      const resolveHand = (finalDealerCards: Card[]) => {
        const playerValue = getHandValue(playerHand.cards).value;
        const dealerValue = getHandValue(finalDealerCards).value;
        const bet = playerHand.bet;

        let result: 'win' | 'loss' | 'push';
        
        if (dealerValue > 21) {
          // Dealer bust - player wins
          setBankroll(prev => prev + bet * 2);
          result = 'win';
          setSessionStats(prev => ({ ...prev, wins: prev.wins + 1, net: prev.net + bet }));
        } else if (playerValue > dealerValue) {
          // Player wins
          setBankroll(prev => prev + bet * 2);
          result = 'win';
          setSessionStats(prev => ({ ...prev, wins: prev.wins + 1, net: prev.net + bet }));
        } else if (playerValue < dealerValue) {
          // Dealer wins - clear bet
          result = 'loss';
          setSessionStats(prev => ({ ...prev, losses: prev.losses + 1, net: prev.net - bet }));
          setCurrentBet(0);
          setBetHistory([]);
        } else {
          // Push - keep bet
          setBankroll(prev => prev + bet);
          result = 'push';
          setSessionStats(prev => ({ ...prev, pushes: prev.pushes + 1 }));
        }

        // On win/push the bet carries to the next hand; revert to the pre-double
        // base so a winning double-down doesn't persist the doubled amount.
        if (result !== 'loss') setCurrentBet(baseBet);

        setPlayerHand(prev => ({ ...prev, result }));
        setHandsPlayed(prev => prev + 1);
        setPhase('complete');
      };

      setTimeout(playDealer, 400);
    };

    dealerPlay();
  }, [phase]); // eslint-disable-line react-hooks/exhaustive-deps

  // Betting functions
  const addBet = (value: number) => {
    if (phase !== 'betting' && phase !== 'complete') return;
    const newBet = currentBet + value;
    if (newBet <= MAX_BET && newBet <= bankroll) {
      setCurrentBet(newBet);
      setBetHistory(prev => [...prev, value]);
    }
  };

  const undoBet = () => {
    if (betHistory.length === 0) return;
    const last = betHistory[betHistory.length - 1];
    setCurrentBet(prev => Math.max(0, prev - last));
    setBetHistory(prev => prev.slice(0, -1));
  };

  const clearBet = () => {
    setCurrentBet(0);
    setBetHistory([]);
  };

  // Deal next hand (single button flow)
  const dealNextHand = useCallback(() => {
    if (handsPlayed >= DEMO_HAND_LIMIT) {
      setPhase('limit-reached');
      return;
    }
    // Reset hands but keep current bet if they won/pushed
    setPlayerHand({ cards: [], bet: 0 });
    setDealerHand({ cards: [], bet: 0 });
    setLastFeedback(null);
    
    // If they have a valid bet, deal immediately
    if (currentBet >= MIN_BET && currentBet <= bankroll) {
      setPhase('betting');
      // Small delay then auto-deal
      setTimeout(() => {
        dealHand();
      }, 100);
    } else {
      // Need to place a bet
      setPhase('betting');
    }
  }, [handsPlayed, currentBet, bankroll, dealHand]);

  // Reset bankroll when player runs out of chips
  const resetBankroll = useCallback(() => {
    setBankroll(STARTING_BANKROLL);
    setCurrentBet(0);
    setBetHistory([]);
    setLastFeedback({ isCorrect: true, message: 'Chips topped up — keep practicing!', correctAction: '' });
  }, []);

  // Hand value display
  const playerValue = getHandValue(playerHand.cards);
  const dealerVisibleCards = dealerHand.cards.filter(c => c.faceUp);
  const dealerValue = getHandValue(dealerVisibleCards);

  // Result display
  const getResultDisplay = () => {
    if (!playerHand.result) return null;
    const displays: Record<string, { text: string; color: string }> = {
      win: { text: 'WIN!', color: 'text-green-400' },
      blackjack: { text: 'BLACKJACK!', color: 'text-yellow-400' },
      loss: { text: 'LOSE', color: 'text-red-400' },
      push: { text: 'PUSH', color: 'text-yellow-400' }
    };
    return displays[playerHand.result];
  };

  // Check if limit reached
  if (phase === 'limit-reached') {
    return (
      <div className="relative rounded-2xl overflow-hidden shadow-2xl">
        {/* Table border */}
        <div className="absolute inset-0 bg-gradient-to-br from-amber-800 via-amber-900 to-amber-950 p-2 sm:p-3 pointer-events-none">
          <div className="w-full h-full rounded-xl bg-gradient-to-br from-green-700 via-green-800 to-green-900" />
        </div>

        {/* Content */}
        <div className="relative m-2 sm:m-3 rounded-xl bg-gradient-to-br from-green-700 via-green-800 to-green-900 p-6 sm:p-8 md:p-12">
          <div className="text-center space-y-6">
            <div className="text-5xl mb-4">🎰</div>
            <h3 className="font-cinzel text-2xl sm:text-3xl font-bold text-white">
              You've played your {DEMO_HAND_LIMIT} demo hands!
            </h3>
            <p className="text-lg text-white/80 max-w-md mx-auto">
              Ready to master basic strategy, learn card counting, and crush the house edge?
            </p>
            <div className="bg-black/30 rounded-xl p-4 inline-block">
              <div className="text-sm text-white/60 mb-2">Your Demo Session</div>
              <div className="flex gap-4 justify-center text-lg font-bold">
                <span className="text-green-400">W:{sessionStats.wins}</span>
                <span className="text-red-400">L:{sessionStats.losses}</span>
                <span className="text-yellow-400">P:{sessionStats.pushes}</span>
              </div>
              <div className={`text-xl font-bold mt-2 ${sessionStats.net >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                ${sessionStats.net >= 0 ? '+' : ''}{sessionStats.net}
              </div>
            </div>
            <button
              onClick={onSignUpClick}
              className="px-8 py-4 rounded-xl bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600 text-black font-bold text-lg shadow-2xl shadow-amber-500/40 transition-all hover:shadow-amber-400/60 hover:scale-105"
            >
              Sign Up to Continue Playing
            </button>
            <p className="text-sm text-white/50">
              Full access to hand trainer, table simulator, speed drills & more
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Determine if we need to show betting vs deal button
  const needsBet = currentBet < MIN_BET;
  const canDeal = currentBet >= MIN_BET && currentBet <= bankroll && !isDealing;

  // Player can never place a legal bet again — surface a reset instead of a dead button.
  const outOfChips = bankroll < MIN_BET && phase === 'betting';

  return (
    <div className="relative rounded-2xl overflow-hidden shadow-2xl">
      {/* Table border - wood frame */}
      <div className="absolute inset-0 bg-gradient-to-br from-amber-800 via-amber-900 to-amber-950 p-2 sm:p-3 pointer-events-none">
        <div className="w-full h-full rounded-xl bg-gradient-to-br from-green-700 via-green-800 to-green-900" />
      </div>

      {/* Felt texture */}
      <div
        className="absolute inset-0 opacity-30 rounded-xl pointer-events-none"
        style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(0,0,0,0.15) 1px, transparent 0)', backgroundSize: '6px 6px' }}
      />

      {/* Content */}
      <div className="relative m-1.5 sm:m-2 rounded-xl p-3 sm:p-4">
        {/* Top bar: Bankroll & Session Stats */}
        <div className="flex justify-between items-start mb-3">
          <div className="bg-gray-900/80 border border-gray-700 rounded-lg px-2 py-1.5">
            <div className="text-[9px] text-gray-400 uppercase tracking-wider">Bankroll</div>
            <div className="text-lg sm:text-xl font-bold text-green-400">${bankroll}</div>
          </div>
          <div className="bg-gray-900/80 border border-gray-700 rounded-lg px-2 py-1.5 text-right">
            <div className="text-[9px] text-gray-400 uppercase tracking-wider">Session</div>
            <div className="flex gap-1.5 text-xs font-bold">
              <span className="text-green-400">W:{sessionStats.wins}</span>
              <span className="text-red-400">L:{sessionStats.losses}</span>
              <span className="text-yellow-400">P:{sessionStats.pushes}</span>
            </div>
            <div className={`text-xs font-bold ${sessionStats.net >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              ${sessionStats.net >= 0 ? '+' : ''}{sessionStats.net}
            </div>
          </div>
        </div>

        {/* Demo badge */}
        <div className="absolute top-3 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full bg-amber-500/20 border border-amber-500/40 text-amber-300 text-[10px] font-medium">
          Demo • {DEMO_HAND_LIMIT - handsPlayed} hands left
        </div>

        {/* Dealer Section */}
        <div className="mb-2 flex flex-col items-center">
          <div className="text-white font-semibold text-base mb-1">Dealer</div>
          <div className="flex justify-center items-center h-[96px] sm:h-[112px] md:h-[144px] pl-6 sm:pl-10">
            {dealerHand.cards.map((card, index) => (
              <div
                key={card.id}
                className="transform transition-all duration-500 -ml-6 sm:-ml-8 relative"
                style={{ zIndex: index }}
              >
                <DemoPlayingCard card={card} index={index} />
              </div>
            ))}
          </div>
          <div className="h-7 flex items-center mt-1">
            {dealerHand.cards.length > 0 && dealerVisibleCards.length > 0 && (
              <div className="text-white font-bold text-lg">
                {dealerValue.soft && dealerValue.value <= 21 && <span className="text-yellow-300">Soft </span>}
                {dealerValue.value}
                {phase === 'complete' && getHandValue(dealerHand.cards).value > 21 && (
                  <span className="text-red-400 ml-2">BUST</span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Decision Feedback */}
        {lastFeedback && (
          <div className={`flex justify-center mb-2 animate-fade-in`}>
            <div className={`px-3 py-1.5 rounded-lg text-xs font-bold ${
              lastFeedback.isCorrect 
                ? 'bg-green-900/60 border border-green-500 text-green-300' 
                : 'bg-red-900/60 border border-red-500 text-red-300'
            }`}>
              {lastFeedback.message}
            </div>
          </div>
        )}

        {/* Game Controls (during play) */}
        {phase === 'playing' && !isDealing && (
          <div className="flex justify-center gap-2 mb-3">
            {[
              { label: 'Hit', onClick: hit, disabled: false },
              { label: 'Stand', onClick: stand, disabled: false },
              { label: 'Double', onClick: doubleDown, disabled: playerHand.cards.length !== 2 || currentBet > bankroll }
            ].map(({ label, onClick, disabled }) => (
              <button
                key={label}
                onClick={onClick}
                disabled={disabled}
                className="px-4 py-2 rounded-full font-bold text-xs transition-all relative overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed min-w-[70px]"
                style={{
                  background: disabled ? '#9ca3af' : 'linear-gradient(135deg, #f9fafb, #e5e7eb, #d1d5db)',
                  borderWidth: '3px',
                  borderColor: disabled ? 'rgba(107, 114, 128, 0.6)' : 'rgba(156, 163, 175, 0.6)',
                  color: '#1f2937',
                  boxShadow: disabled ? 'none' : '0 2px 6px rgba(0,0,0,0.18)'
                }}
              >
                <div className="absolute inset-0.5 rounded-full border border-dashed border-gray-400/30 pointer-events-none" />
                <span className="relative">{label}</span>
              </button>
            ))}
          </div>
        )}

        {/* Deal Button - single button for both initial deal and next hand */}
        {(phase === 'betting' || phase === 'complete') && playerHand.cards.length === 0 && (
          <div className="flex justify-center mb-3">
            <button
              onClick={dealHand}
              disabled={!canDeal}
              className="px-5 py-2 rounded-full font-bold text-sm text-white shadow-lg transition-all transform hover:scale-105 relative overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                background: 'linear-gradient(135deg, #ef4444, #dc2626, #b91c1c)',
                borderWidth: '4px',
                borderColor: 'rgba(239, 68, 68, 0.5)'
              }}
            >
              <div className="absolute inset-1 rounded-full border-2 border-dashed border-white/30 pointer-events-none" />
              <span className="relative">Deal Hand</span>
            </button>
          </div>
        )}

        {/* Deal Next Hand button after hand completes */}
        {phase === 'complete' && playerHand.cards.length > 0 && (
          <div className="flex justify-center mb-3">
            <button
              onClick={dealNextHand}
              disabled={needsBet}
              className="px-5 py-2 rounded-full font-bold text-sm text-white shadow-lg transition-all transform hover:scale-105 relative overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                background: 'linear-gradient(135deg, #ef4444, #dc2626, #b91c1c)',
                borderWidth: '4px',
                borderColor: 'rgba(239, 68, 68, 0.5)'
              }}
            >
              <div className="absolute inset-1 rounded-full border-2 border-dashed border-white/30 pointer-events-none" />
              <span className="relative">{needsBet ? 'Place a Bet First' : 'Deal Next Hand'}</span>
            </button>
          </div>
        )}

        {/* Player Section */}
        <div className="mb-2 flex flex-col items-center">
          <div className="text-white font-semibold text-base mb-1">Player</div>
          <div className="flex justify-center items-center h-[96px] sm:h-[112px] md:h-[144px] pl-6 sm:pl-10">
            {playerHand.cards.map((card, index) => (
              <div
                key={card.id}
                className="transform transition-all duration-500 -ml-6 sm:-ml-8 relative hover:-translate-y-2"
                style={{ zIndex: index }}
              >
                <DemoPlayingCard card={card} index={index} />
              </div>
            ))}
          </div>
          <div className="h-7 flex items-center mt-1">
            {playerHand.cards.length > 0 && (
              <div className="text-white font-bold text-lg">
                {playerValue.value > 21 ? (
                  <span className="text-red-400">BUST ({playerValue.value})</span>
                ) : isBlackjack(playerHand.cards) && playerHand.cards.length === 2 ? (
                  <span className="text-yellow-400">BLACKJACK!</span>
                ) : (
                  <>
                    {playerValue.soft && <span className="text-yellow-300">Soft </span>}
                    {playerValue.value}
                  </>
                )}
              </div>
            )}
          </div>
          {/* Result display */}
          <div className="h-9 flex items-center">
            {playerHand.result && getResultDisplay() && (
              <div className={`text-2xl font-bold animate-pulse ${getResultDisplay()!.color}`}>
                {getResultDisplay()!.text}
              </div>
            )}
          </div>
        </div>

        {/* Betting Section */}
        {outOfChips ? (
          <div className="text-center space-y-3 py-4">
            <div className="text-amber-300 font-bold">You're out of chips for this session.</div>
            <button
              onClick={resetBankroll}
              className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 text-black font-bold shadow-lg hover:scale-105 transition-transform"
            >
              Reset chips (${STARTING_BANKROLL})
            </button>
            <div className="text-xs text-white/50">Resetting chips doesn't use one of your demo hands.</div>
          </div>
        ) : (
          <div className="bg-black/30 rounded-lg p-2 sm:p-3">
            <div className="flex flex-wrap items-center justify-center gap-2">
              {/* Current Bet */}
              <div className="bg-gray-900/80 border-2 border-yellow-500 rounded-lg px-3 py-2 text-center min-w-[80px]">
                <div className="text-[10px] text-gray-300">Current Bet</div>
                <div className="text-lg font-bold text-yellow-400">{currentBet}</div>
                {needsBet && phase !== 'playing' && phase !== 'dealer-turn' && (
                  <div className="text-xs text-red-400">Min: ${MIN_BET}</div>
                )}
              </div>

              {/* Chips */}
              {chips.map((chip) => (
                <button
                  key={chip.value}
                  onClick={() => addBet(chip.value)}
                  disabled={phase === 'playing' || phase === 'dealer-turn' || currentBet + chip.value > MAX_BET || currentBet + chip.value > bankroll}
                  className={`
                    relative w-12 h-12 sm:w-14 sm:h-14 rounded-full border-3
                    ${chip.color} font-extrabold text-xs sm:text-sm ${chip.textColor}
                    shadow-lg overflow-hidden transform transition-all duration-150
                    hover:scale-110 active:scale-95
                    disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100
                    flex items-center justify-center
                  `}
                  style={{
                    backgroundImage: chip.pattern,
                    boxShadow: '0 3px 6px rgba(0, 0, 0, 0.3), inset 0 -2px 4px rgba(0, 0, 0, 0.2)'
                  }}
                >
                  <div className="absolute inset-[6px] rounded-full border border-white/30 opacity-70" />
                  <div className="absolute inset-1.5 rounded-full bg-white border border-gray-200 shadow-inner flex items-center justify-center">
                    <span className="text-gray-800 font-black text-xs sm:text-sm">{chip.value}</span>
                  </div>
                </button>
              ))}

              {/* Undo/Clear */}
              <div className="flex flex-col gap-1">
                <button
                  onClick={undoBet}
                  disabled={betHistory.length === 0 || phase === 'playing' || phase === 'dealer-turn'}
                  className="px-3 py-1.5 rounded-full font-bold text-xs transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{
                    background: 'linear-gradient(135deg, #f9fafb, #e5e7eb)',
                    borderWidth: '2px',
                    borderColor: 'rgba(156, 163, 175, 0.6)',
                    color: '#1f2937'
                  }}
                >
                  Undo
                </button>
                <button
                  onClick={clearBet}
                  disabled={currentBet === 0 || phase === 'playing' || phase === 'dealer-turn'}
                  className="px-3 py-1.5 rounded-full font-bold text-xs transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{
                    background: 'linear-gradient(135deg, #f9fafb, #e5e7eb)',
                    borderWidth: '2px',
                    borderColor: 'rgba(156, 163, 175, 0.6)',
                    color: '#1f2937'
                  }}
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
